import React, { useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, Users, X, Search, Trash2, RefreshCw,
  Loader, UserPlus, FileSpreadsheet
} from 'lucide-react';
import { sections } from '../services/api';
import { generateTermOptions, formatTermDisplay } from '../utils/termUtils';
import RosterImportModal from '../components/RosterImportModal';
import SectionLabsStep from '../components/SectionLabsStep';
import { Dialog, DialogHeader, DialogBody, DialogFooter, Field } from '../components/Dialog';

function Sections() {
  const [searchParams] = useSearchParams();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showRosterModal, setShowRosterModal] = useState(null);
  const [rosterStep, setRosterStep] = useState(null);
  const [termFilter, setTermFilter] = useState(searchParams.get('term') || '');
  const [courseFilter, setCourseFilter] = useState(searchParams.get('course') || '');
  const [deleteError, setDeleteError] = useState('');
  const queryClient = useQueryClient();
  const { terms } = useMemo(() => generateTermOptions(), []);

  const urlSection = searchParams.get('section');
  const urlOpenedRef = useRef(false);

  const { data: sectionsData, isLoading } = useQuery({
    queryKey: ['sections', termFilter, courseFilter],
    queryFn: () => sections.list({
      term: termFilter || undefined,
      courseCode: courseFilter || undefined,
    }),
  });

  const sectionsList = sectionsData || [];

  // Auto-open roster modal for matching section from URL params (one-shot)
  useEffect(() => {
    if (!urlSection || urlOpenedRef.current || sectionsList.length === 0) return;
    const match = sectionsList.find(s => s.sectionNumber === urlSection);
    if (match) {
      urlOpenedRef.current = true;
      setShowRosterModal(match._id);
    }
  }, [urlSection, sectionsList]);

  const deleteMutation = useMutation({
    mutationFn: (id) => sections.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
      setDeleteError('');
    },
    onError: (err) => setDeleteError(err.response?.data?.message || 'Failed to delete section'),
  });

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  return (
    <div className="animate-fade-in">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ marginBottom: 8 }}>Sections</h1>
          <p className="text-muted">Manage reusable student rosters for your courses</p>
        </div>
        <button className="btn btn-primary" onClick={() => setShowCreateModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Plus size={18} /> Create Section
        </button>
      </div>

      {/* Inline feedback */}
      {deleteError && (
        <div role="alert" style={{
          padding: '10px 16px', marginBottom: 16, borderRadius: 8,
          background: 'var(--btn-danger-bg)', border: '1px solid var(--accent-danger)',
          color: 'var(--accent-danger)', fontSize: 14,
        }}>
          {deleteError}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24 }}>
        <select
          value={termFilter}
          onChange={(e) => setTermFilter(e.target.value)}
          style={{
            padding: '10px 12px',
            background: 'var(--bg-tertiary)',
            border: '1px solid var(--border-primary)',
            borderRadius: 6,
            color: 'var(--text-primary)',
            fontSize: 14,
            minWidth: 180,
          }}
        >
          <option value="">All Terms</option>
          {terms.map(t => (
            <option key={t.code} value={t.code}>{t.display}</option>
          ))}
        </select>
        <div style={{ position: 'relative', flex: 1, maxWidth: 300 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={courseFilter}
            onChange={(e) => setCourseFilter(e.target.value.toUpperCase())}
            placeholder="Filter by course code..."
            style={{ paddingLeft: 36, width: '100%' }}
          />
        </div>
      </div>

      {/* Section Cards */}
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
          <Loader size={32} className="spinner" />
        </div>
      ) : sectionsList.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: 60,
          color: 'var(--text-muted)',
        }}>
          <Users size={48} style={{ marginBottom: 16, opacity: 0.3 }} />
          <p style={{ fontSize: 16, marginBottom: 8 }}>No sections found</p>
          <p style={{ fontSize: 14 }}>Create a section to build a reusable student roster</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
          {sectionsList.map(section => (
            <div
              key={section._id}
              style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-primary)',
                borderRadius: 12,
                padding: 20,
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{
                      background: 'var(--badge-info-bg)',
                      color: 'var(--accent-primary)',
                      padding: '2px 8px',
                      borderRadius: 4,
                      fontSize: 11,
                      fontWeight: 600,
                    }}>
                      {formatTermDisplay(section.term)}
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 15 }}>{section.courseCode}</span>
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 600, color: 'var(--text-primary)' }}>
                    S{section.sectionNumber}
                    {section.name && <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 8 }}>— {section.name}</span>}
                  </div>
                </div>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  background: 'var(--bg-tertiary)',
                  padding: '4px 10px',
                  borderRadius: 6,
                }}>
                  <Users size={14} style={{ color: 'var(--text-muted)' }} />
                  <span style={{ fontSize: 14, fontWeight: 600 }}>{section.studentCount || 0}</span>
                </div>
              </div>

              {section.createdBy?.name && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                  Created by {section.createdBy.name}
                </div>
              )}

              <div style={{ marginTop: 'auto', display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid var(--border-primary)' }}>
                <button
                  className="btn btn-primary"
                  onClick={() => setShowRosterModal(section._id)}
                  style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 13, padding: '8px 12px' }}
                >
                  <UserPlus size={14} /> Manage Roster
                </button>
                <button
                  className="btn"
                  onClick={() => { setRosterStep('sync'); setShowRosterModal(section._id); }}
                  title="Create missing environments in this section's labs"
                  style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, padding: '8px 12px' }}
                >
                  <RefreshCw size={14} /> Sync
                </button>
                {confirmDeleteId === section._id ? (
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button
                      onClick={() => { deleteMutation.mutate(section._id); setConfirmDeleteId(null); }}
                      style={{
                        background: 'var(--accent-danger)',
                        border: 'none',
                        borderRadius: 6,
                        color: '#fff',
                        cursor: 'pointer',
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 600,
                      }}
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDeleteId(null)}
                      style={{
                        background: 'none',
                        border: '1px solid var(--border-primary)',
                        borderRadius: 6,
                        color: 'var(--text-secondary)',
                        cursor: 'pointer',
                        padding: '8px 10px',
                        fontSize: 12,
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setConfirmDeleteId(section._id)}
                    title="Delete section"
                    style={{
                      background: 'none',
                      border: '1px solid var(--border-primary)',
                      borderRadius: 6,
                      color: 'var(--accent-danger)',
                      cursor: 'pointer',
                      padding: '8px 10px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateSectionModal
          onClose={() => setShowCreateModal(false)}
          onCreated={() => {
            setShowCreateModal(false);
            queryClient.invalidateQueries({ queryKey: ['sections'] });
          }}
        />
      )}

      {showRosterModal && (
        <SectionRosterModal
          sectionId={showRosterModal}
          initialStep={rosterStep}
          onClose={() => { setShowRosterModal(null); setRosterStep(null); }}
        />
      )}
    </div>
  );
}

function CreateSectionModal({ onClose, onCreated }) {
  const { terms, defaultTerm } = useMemo(() => generateTermOptions(), []);
  const [term, setTerm] = useState(defaultTerm);
  const [courseCode, setCourseCode] = useState('');
  const [sectionNumber, setSectionNumber] = useState('');
  const [name, setName] = useState('');
  // Structured student objects straight from the roster parser. Previously the
  // imported list was flattened back into "id, name, email" text and re-split on
  // commas, which is how PeopleSoft rosters got mangled: their Name column is
  // "Surname,First", so the first name landed in the email field.
  const [importedStudents, setImportedStudents] = useState([]);
  const [error, setError] = useState('');
  const [step, setStep] = useState('form');
  const [showImport, setShowImport] = useState(false);

  const handleImport = (students) => {
    setShowImport(false);
    // Merge by studentId so a second import cannot duplicate a row.
    setImportedStudents((prev) => {
      const byId = new Map(prev.map((s) => [s.studentId, s]));
      students.forEach((s) => byId.set(s.studentId, s));
      return [...byId.values()];
    });
  };

  const createMutation = useMutation({
    mutationFn: async (data) => {
      const result = await sections.create(data);
      const sectionId = result.section?._id;

      if (sectionId && importedStudents.length > 0) {
        setStep('adding');
        await sections.addStudents(sectionId, { students: importedStudents });
      }

      return result;
    },
    onSuccess: () => onCreated(),
    onError: (err) => {
      setStep('form');
      setError(err.response?.data?.message || 'Failed to create section');
    },
  });

  const isPending = createMutation.isPending;

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!term) { setError('Please select a term'); return; }
    if (!courseCode) { setError('Please enter a course code'); return; }
    if (!sectionNumber) { setError('Please enter a section number'); return; }

    createMutation.mutate({
      term,
      courseCode: courseCode.toUpperCase(),
      sectionNumber,
      name: name || undefined,
    });
  };

  const selectStyle = {
    width: '100%', padding: '10px 12px', background: 'var(--bg-tertiary)',
    border: '1px solid var(--border-primary)', borderRadius: 6,
    color: 'var(--text-primary)', fontSize: 14,
  };

  return (
    <>
    {showImport && (
      <RosterImportModal onClose={() => setShowImport(false)} onImport={handleImport} />
    )}
    <Dialog
      size="md"
      onClose={isPending ? undefined : onClose}
      labelledBy="create-section-title"
      isDirty={Boolean(courseCode || sectionNumber || name || importedStudents.length || term !== defaultTerm)}
    >
      <DialogHeader id="create-section-title" title="Create Section" icon={<Users size={20} />} onClose={isPending ? undefined : onClose} />

      {step === 'adding' ? (
        <DialogBody>
          <div style={{ padding: 32, textAlign: 'center' }}>
            <Loader size={32} className="spinner" style={{ marginBottom: 16 }} />
            <p>Adding students to section roster...</p>
          </div>
        </DialogBody>
      ) : (
        <form className="dlg-form" onSubmit={handleSubmit} aria-label="Create new section">
          <DialogBody>
            {error && <div role="alert" className="dlg-alert">{error}</div>}

            <Field label="Term" required>
              <select value={term} onChange={(e) => setTerm(e.target.value)} style={selectStyle} required>
                {terms.map(t => (
                  <option key={t.code} value={t.code}>{t.display} ({t.code})</option>
                ))}
              </select>
            </Field>

            <div className="form-grid form-grid-21">
              <Field label="Course Code" required>
                <input
                  type="text"
                  value={courseCode}
                  onChange={(e) => setCourseCode(e.target.value.replace(/\s/g, '').toUpperCase())}
                  placeholder="INFS3201"
                  required
                  style={{ textTransform: 'uppercase' }}
                />
              </Field>
              <Field label="Section #" required>
                <input
                  type="text"
                  value={sectionNumber}
                  onChange={(e) => setSectionNumber(e.target.value)}
                  placeholder="1"
                  required
                />
              </Field>
            </div>

            <Field label="Name (optional)">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Monday Morning Lab"
              />
            </Field>

            <Field
              label={
                <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                  <span>Initial Student Roster (optional)</span>
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setShowImport(true)}
                    disabled={isPending}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 10px' }}
                  >
                    <FileSpreadsheet size={12} /> Import from Excel
                  </button>
                </span>
              }
              hint="Upload the PeopleSoft class roster export. Student IDs, names and UDST email addresses are read directly from the file."
            >
              {importedStudents.length === 0 ? (
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0 }}>
                  No roster imported yet — the section will be created empty. You can
                  import students now or from the roster view afterwards.
                </p>
              ) : (
                <div role="status" style={{
                  padding: '10px 12px', background: 'var(--badge-info-bg)',
                  border: '1px solid var(--accent-primary)', borderRadius: 6, fontSize: 13,
                }}>
                  <strong>{importedStudents.length}</strong> student{importedStudents.length === 1 ? '' : 's'} ready to import
                  <button
                    type="button"
                    className="btn"
                    onClick={() => setImportedStudents([])}
                    disabled={isPending}
                    style={{ marginLeft: 12, fontSize: 12, padding: '4px 8px' }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </Field>
          </DialogBody>

          <DialogFooter>
            <button type="button" className="btn" onClick={onClose} disabled={isPending}>Cancel</button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isPending}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              {isPending ? <Loader size={16} className="spinner" /> : <Plus size={16} />}
              Create Section
            </button>
          </DialogFooter>
        </form>
      )}
    </Dialog>
    </>
  );
}

function SectionRosterModal({ sectionId, onClose, initialStep }) {
  const [addError, setAddError] = useState('');
  const [addResult, setAddResult] = useState(null);
  // The "put them into the section's labs" step: after an import, for the
  // students just added; from Sync, for the whole roster.
  const [labStep, setLabStep] = useState(initialStep === 'sync' ? { studentIds: null, run: 1 } : null);
  const [removingIds, setRemovingIds] = useState(new Set());
  const [showImport, setShowImport] = useState(false);
  const queryClient = useQueryClient();

  const { data: section, isLoading } = useQuery({
    queryKey: ['section', sectionId],
    queryFn: () => sections.get(sectionId),
  });

  const addMutation = useMutation({
    mutationFn: (data) => sections.addStudents(sectionId, data),
    onSuccess: (data) => {
      setAddResult(data.results);
      if (data.results?.added?.length) {
        setLabStep((prev) => ({ studentIds: data.results.added, quiet: true, run: (prev?.run || 0) + 1 }));
      }
      queryClient.invalidateQueries({ queryKey: ['section', sectionId] });
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
    onError: (err) => setAddError(err.response?.data?.message || 'Failed to add students'),
  });

  const removeMutation = useMutation({
    mutationFn: async (studentUserId) => {
      setRemovingIds(prev => new Set([...prev, studentUserId]));
      try {
        return await sections.removeStudent(sectionId, studentUserId);
      } finally {
        setRemovingIds(prev => { const n = new Set(prev); n.delete(studentUserId); return n; });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['section', sectionId] });
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
    onError: (err) => setAddError(err.response?.data?.message || 'Failed to remove student'),
  });

  const handleImport = (students) => {
    setShowImport(false);
    setAddError('');
    setAddResult(null);
    addMutation.mutate({ students });
  };

  const activeStudents = section?.students?.filter(s => s.isActive) || [];

  return (
    <>
    {showImport && (
      <RosterImportModal onClose={() => setShowImport(false)} onImport={handleImport} />
    )}
    <Dialog size="md" onClose={onClose} labelledBy="roster-modal-title" confirmOnClose={false}>
      <DialogHeader
        id="roster-modal-title"
        icon={<Users size={20} />}
        onClose={onClose}
        title={
          <>
            {section ? `${section.courseCode} — S${section.sectionNumber}` : 'Section Roster'}
            {section?.name && <span style={{ fontSize: 14, fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 8 }}>({section.name})</span>}
          </>
        }
      />

      <DialogBody>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <Loader size={24} className="spinner" />
            </div>
          ) : (
            <>
              {labStep && (
                <SectionLabsStep
                  key={labStep.run}
                  sectionId={sectionId}
                  studentIds={labStep.studentIds}
                  quietIfNothingToDo={!!labStep.quiet}
                  onClose={() => setLabStep(null)}
                />
              )}

              {/* Current Students */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>
                    Current Students ({activeStudents.length})
                  </h4>
                  <button
                    className="btn"
                    onClick={() => setLabStep((prev) => ({ studentIds: null, run: (prev?.run || 0) + 1 }))}
                    title="Create missing environments in this section's labs"
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 10px' }}
                  >
                    <RefreshCw size={12} /> Sync to Labs
                  </button>
                </div>

                {activeStudents.length === 0 ? (
                  <div style={{
                    padding: 24,
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    background: 'var(--bg-tertiary)',
                    borderRadius: 8,
                    fontSize: 14,
                  }}>
                    No students in this section yet
                  </div>
                ) : (
                  <div style={{
                    border: '1px solid var(--border-primary)',
                    borderRadius: 8,
                    overflow: 'hidden',
                    maxHeight: 250,
                    overflowY: 'auto',
                  }}>
                    {activeStudents.map((s, i) => (
                      <div
                        key={s._id || s.studentId?._id || i}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 12px',
                          borderBottom: i < activeStudents.length - 1 ? '1px solid var(--border-primary)' : 'none',
                          background: i % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-tertiary)',
                          fontSize: 13,
                        }}
                      >
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, width: 120, flexShrink: 0 }}>
                          {s.studentId?.studentId || '—'}
                        </span>
                        <span style={{ flex: 1, color: 'var(--text-secondary)' }}>
                          {s.studentId?.name || '—'}
                        </span>
                        <span style={{ flex: 1, color: 'var(--text-muted)', fontSize: 12 }}>
                          {s.studentId?.email || '—'}
                        </span>
                        <button
                          onClick={() => {
                            if (s.studentId?._id) {
                              removeMutation.mutate(s.studentId._id);
                            }
                          }}
                          disabled={removingIds.has(s.studentId?._id)}
                          title="Remove from roster"
                          style={{
                            background: 'none',
                            border: 'none',
                            color: 'var(--accent-danger)',
                            cursor: 'pointer',
                            padding: 4,
                            borderRadius: 4,
                            display: 'flex',
                            alignItems: 'center',
                          }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Add Students */}
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-secondary)' }}>
                    Add Students
                  </h4>
                  <button
                    className="btn"
                    onClick={() => setShowImport(true)}
                    style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, padding: '6px 10px' }}
                  >
                    <FileSpreadsheet size={12} /> Import from Excel
                  </button>
                </div>

                {addError && (
                  <div role="alert" style={{
                    padding: '8px 12px',
                    background: 'var(--btn-danger-bg)',
                    border: '1px solid var(--accent-danger)',
                    borderRadius: 6,
                    marginBottom: 12,
                    color: 'var(--accent-danger)',
                    fontSize: 13,
                  }}>
                    {addError}
                  </div>
                )}

                {addResult && (
                  <div role="status" style={{
                    padding: '8px 12px',
                    background: 'var(--badge-info-bg)',
                    border: '1px solid var(--accent-primary)',
                    borderRadius: 6,
                    marginBottom: 12,
                    fontSize: 13,
                  }}>
                    <span style={{ color: 'var(--accent-primary)' }}>
                      Added: {addResult.added?.length || 0} | Already in roster: {addResult.alreadyInRoster?.length || 0}
                      {addResult.created?.length > 0 && ` | Created: ${addResult.created.length}`}
                      {addResult.failed?.length > 0 && ` | Failed: ${addResult.failed.length}`}
                    </span>
                  </div>
                )}

                {/* Students are added exclusively by importing the PeopleSoft
                    roster export. The former free-text box parsed "id, name, email"
                    by splitting on commas, but PeopleSoft's Name column is
                    "Surname,First" — so pasting rows put the first name into the
                    email field and left the surname as the whole name. */}
                <p style={{ fontSize: 13, color: 'var(--text-muted)', margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {addMutation.isPending
                    ? <><Loader size={14} className="spinner" /> Adding students…</>
                    : <><UserPlus size={14} /> Use <strong>Import from Excel</strong> above to add students from the class roster export.</>}
                </p>
              </div>
            </>
          )}
      </DialogBody>
    </Dialog>
    </>
  );
}

export default Sections;
